/**
 * Blitzortung.org real-time lightning data client.
 *
 * The public Blitzortung websocket (wss://ws{1,7,8}.blitzortung.org) streams
 * every detected strike worldwide (~40-60/s during active weather). Frames are
 * LZW-compressed JSON; the subscription is started by sending {"a": 111}.
 *
 * Data usage policy (https://www.blitzortung.org): attribution required —
 * consumers must display "Blitzortung.org" next to the data.
 */

export type LightningStrike = {
  /** Stable identity for React keys and dedupe across reconnects. */
  id: string;
  /** Strike time, epoch milliseconds. */
  time: number;
  lat: number;
  lon: number;
};

export type BlitzortungRawStrike = {
  /** Strike time, epoch nanoseconds. */
  time: number;
  lat: number;
  lon: number;
};

export type BlitzortungClientOptions = {
  onStrike: (strike: LightningStrike) => void;
  onConnectionChange?: (connected: boolean) => void;
  hosts?: string[];
  /** Base reconnect delay; doubles per consecutive failure. */
  reconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
};

export const BLITZORTUNG_HOSTS = [
  'wss://ws1.blitzortung.org/',
  'wss://ws7.blitzortung.org/',
  'wss://ws8.blitzortung.org/',
];

const SUBSCRIBE_MESSAGE = '{"a":111}';
const DEFAULT_RECONNECT_DELAY_MS = 3000;
const DEFAULT_MAX_RECONNECT_DELAY_MS = 60000;
const NS_PER_MS = 1e6;

/**
 * LZW string decompression used by the Blitzortung websocket frames
 * (the same algorithm as map.blitzortung.org's own client).
 */
export const decode = (data: string): string => {
  if (data.length === 0) {
    return data;
  }
  const dict: Record<number, string> = {};
  const chars = data.split('');
  let currChar = chars[0];
  let oldPhrase = currChar;
  const out = [currChar];
  let code = 256;
  let phrase: string;
  for (let i = 1; i < chars.length; i++) {
    const currCode = chars[i].charCodeAt(0);
    if (currCode < 256) {
      phrase = chars[i];
    } else {
      phrase = dict[currCode] ? dict[currCode] : oldPhrase + currChar;
    }
    out.push(phrase);
    currChar = phrase.charAt(0);
    dict[code] = oldPhrase + currChar;
    code++;
    oldPhrase = phrase;
  }
  return out.join('');
};

const parseStrike = (raw: string): LightningStrike | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    try {
      parsed = JSON.parse(decode(raw));
    } catch {
      return null;
    }
  }
  const strike = parsed as Partial<BlitzortungRawStrike> | null;
  if (
    strike == null ||
    typeof strike.time !== 'number' ||
    typeof strike.lat !== 'number' ||
    typeof strike.lon !== 'number'
  ) {
    return null;
  }
  const timeMs = Math.floor(strike.time / NS_PER_MS);
  return {
    id: `${strike.time}:${strike.lat}:${strike.lon}`,
    time: timeMs,
    lat: strike.lat,
    lon: strike.lon,
  };
};

/**
 * Resilient Blitzortung websocket client: rotates through hosts on failure
 * with exponential backoff, decodes frames, and emits normalized strikes.
 */
export class BlitzortungClient {
  private readonly options: BlitzortungClientOptions;
  private readonly hosts: string[];
  private ws: WebSocket | null = null;
  private hostIndex = 0;
  private failureCount = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;

  constructor(options: BlitzortungClientOptions) {
    this.options = options;
    this.hosts =
      options.hosts && options.hosts.length > 0
        ? options.hosts
        : BLITZORTUNG_HOSTS;
  }

  start() {
    if (!this.stopped) {
      return;
    }
    this.stopped = false;
    this.failureCount = 0;
    this.connect();
  }

  stop() {
    this.stopped = true;
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    if (ws != null) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        // Already closed/failed — nothing to release.
      }
    }
  }

  private connect() {
    const url = this.hosts[this.hostIndex % this.hosts.length];
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.stopped) {
        return;
      }
      this.failureCount = 0;
      ws.send(SUBSCRIBE_MESSAGE);
      this.options.onConnectionChange?.(true);
    };

    ws.onmessage = (event: WebSocketMessage) => {
      if (this.stopped || typeof event.data !== 'string') {
        return;
      }
      const strike = parseStrike(event.data);
      if (strike != null) {
        this.options.onStrike(strike);
      }
    };

    ws.onerror = () => {
      // onclose always follows; reconnect is handled there.
    };

    ws.onclose = () => {
      if (this.stopped) {
        return;
      }
      this.ws = null;
      this.options.onConnectionChange?.(false);
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.stopped || this.reconnectTimer != null) {
      return;
    }
    this.hostIndex++;
    const base = this.options.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS;
    const max =
      this.options.maxReconnectDelayMs ?? DEFAULT_MAX_RECONNECT_DELAY_MS;
    const delay = Math.min(base * 2 ** this.failureCount, max);
    this.failureCount++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) {
        this.connect();
      }
    }, delay);
  }
}

// React Native's WebSocket message event is not in the DOM lib typings under
// the same name; structurally both expose `data`. Deliberately NOT named
// `WebSocketMessageEvent`: React Native declares a global interface under that
// name, and a module-local alias shadowing it makes the handler unassignable
// ("two different types with this name exist") when core is compiled from the
// mobile app.
type WebSocketMessage = {data?: unknown};
